import { Checkbox, CollapseProps, Divider, Form, Input, Row, Select } from "antd";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SystemDescriptions } from "src/common/descriptions";
import { QuotationDetail } from "src/common/models/quotation";
import { Service } from "src/common/models/service";
import { getInputRequiredRule, selectFilterOption } from "src/common/util";
import { Col24, Col8 } from "src/components/Columns";
import { CurrencyInput } from "src/components/CurrencyInput";
import { FormActionButtons } from "src/components/FormActionButtons";
import { getServicesApi } from "src/features/Service/state/action";
import { RootState } from "src/state/reducer";
import { v4 as uuidv4 } from 'uuid';
import * as Styles from './styles';
import { CharacteristicsForm } from "src/features/Service/ServiceForm/CharacteristicsForm";
import { ScheduleDayKey, ServiceSchedule, WeeklyScheduleDays } from "src/common/models/schedule";

interface Props {
    onFinish: (detailItem: QuotationDetail) => void
    onCancel: () => void
    initialValues?: QuotationDetail
}

export type ReduxProps = {
    services?: Service[]
}

export const QuotationItemForm = (props: Props) => {
    const dispatch = useDispatch()

    const [form] = Form.useForm();

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        services: state.service.services,
    }))

    const [itemCharacteristics, setItemCharacteristics] = useState<string[]>([])
    const [itemSchedules, setItemSchedules] = useState<ServiceSchedule[]>([])
    const [scheduleEnabled, setScheduleEnabled] = useState<boolean>(false)
    const hasRequestedServices = useRef<boolean>(false)

    const descriptions = SystemDescriptions.PAGES.QUOTATION.CREATE
    const scheduleDayOptions: { label: string, value: ScheduleDayKey }[] = [
        { label: "Lunes", value: "monday" },
        { label: "Martes", value: "tuesday" },
        { label: "Miercoles", value: "wednesday" },
        { label: "Jueves", value: "thursday" },
        { label: "Viernes", value: "friday" },
        { label: "Sabado", value: "saturday" },
        { label: "Domingo", value: "sunday" },
    ]

    const getSelectedScheduleDays = (schedule?: ServiceSchedule): ScheduleDayKey[] => (
        scheduleDayOptions
            .map(option => option.value)
            .filter(day => Boolean(schedule?.days?.[day]))
    )

    const buildSchedules = (days?: ScheduleDayKey[], note?: string): ServiceSchedule[] => {
        if (!days || days.length === 0) {
            return []
        }

        return [{
            type: "WEEKLY",
            days: scheduleDayOptions.reduce<WeeklyScheduleDays>((acc, option) => ({
                ...acc,
                [option.value]: days.includes(option.value),
            }), {}),
            note,
        }]
    }

    useEffect(() => {
        if (!hasRequestedServices.current && (!reduxProps.services || reduxProps.services.length === 0)) {
            hasRequestedServices.current = true
            dispatch(getServicesApi())
        }
    }, [dispatch, reduxProps.services])

    useEffect(() => {
        if (props.initialValues) {
            const selectedService = props.initialValues.service || props.initialValues.serviceId
            const initialSchedule = props.initialValues.schedules?.[0]
            form.setFieldsValue({
                qty: props.initialValues.qty,
                service: selectedService ? selectedService.toString() : undefined,
                description: props.initialValues.description,
                price: props.initialValues.price,
                total: props.initialValues.total,
                hasSchedule: Boolean(initialSchedule),
                scheduleDays: getSelectedScheduleDays(initialSchedule),
                scheduleNote: initialSchedule?.note,
            })

            setItemCharacteristics(props.initialValues.characteristics || [])
            setItemSchedules(props.initialValues.schedules || [])
            setScheduleEnabled(Boolean(initialSchedule))
        }
    }, [props.initialValues, form])

    const onFinish = (formValues) => {
        const foundService = reduxProps.services?.find(s => s.id?.toString() === formValues.service?.toString())
        const hasSelectedService = Boolean(formValues.service)
        const serviceChanged = formValues.service?.toString() !== props.initialValues?.serviceId?.toString()
        const detailItem: QuotationDetail = {
            id: props.initialValues?.id,
            qty: Number(formValues.qty),
            service: formValues.service,
            serviceId: hasSelectedService ? Number(formValues.service) : undefined,
            serviceName: foundService?.name || (hasSelectedService ? props.initialValues?.serviceName : undefined) || formValues.description,
            serviceCategory: foundService?.serviceCategory || (hasSelectedService ? props.initialValues?.serviceCategory : undefined),
            description: formValues.description,
            price: formValues.price,
            total: formValues.total,
            ref: props.initialValues?.ref || uuidv4(),
            characteristics: itemCharacteristics,
            imageUrl: foundService?.imageUrl || (!serviceChanged ? props.initialValues?.imageUrl : undefined),
            schedules: scheduleEnabled ? buildSchedules(formValues.scheduleDays, formValues.scheduleNote) : [],
        }
        form.resetFields();
        props.onFinish(detailItem)
    }

    const getButtonStatus = (): boolean => {
        return !form.isFieldsTouched(["qty", "price", "description"]) ||
            form.getFieldsError().filter(({ errors }) => errors.length)
                .length > 0
    }

    const getPositiveNumberRule = (label: string) => ({
        validator: (_: any, value: any) => {
            const numericValue = Number(value)
            if (!value || Number.isNaN(numericValue) || numericValue <= 0) {
                return Promise.reject(new Error(descriptions.DETAIL.FORM.GREATER_THAN_ZERO(label)))
            }

            return Promise.resolve()
        },
    })

    const renderButtons = () => (
        <FormActionButtons
            cancelText={descriptions.DETAIL.FORM.CANCEL}
            actionText={descriptions.DETAIL.FORM.ACCEPT}
            onCancel={() => {
                form.resetFields()
                props.onCancel()
            }}
            actionDisabled={getButtonStatus}
        />
    )

    const handleQtyPriceChange = () => {
        const qty = form.getFieldValue("qty") || 0
        const price = form.getFieldValue("price") || 0
        const total = qty * price
        form.setFieldValue("total", total)
    }

    const handleServiceChange = (e) => {
        console.log("service", e)

        const foundService = reduxProps.services?.find(s => s.id?.toString() === e)
        if (foundService) {
            form.setFieldsValue({
                description: foundService.name,
                price: foundService.price,
                total: foundService.price,
                qty: 1,
            })
            setItemCharacteristics(foundService?.characteristics || [])
            setItemSchedules(foundService?.schedules || [])
            setScheduleEnabled(Boolean(foundService?.schedules && foundService.schedules.length > 0))
            form.setFieldsValue({
                hasSchedule: Boolean(foundService?.schedules && foundService.schedules.length > 0),
                scheduleDays: getSelectedScheduleDays(foundService?.schedules?.[0]),
                scheduleNote: foundService?.schedules?.[0]?.note,
            })
            return
        }

        setItemSchedules([])
        setScheduleEnabled(false)
        form.setFieldsValue({ hasSchedule: false, scheduleDays: [], scheduleNote: undefined })
    }

    const characteristicsChange = (characteristics: string[]) => {
        setItemCharacteristics(characteristics)
    }

    const items: CollapseProps['items'] = [
        {
            key: "CHARACTERISTICS",
            label: <span><b>{descriptions.DETAIL.CHARACTERISTICS} - ({itemCharacteristics.length})</b></span>,
            children: <CharacteristicsForm
                acceptOnChange
                onCancel={() => { }}
                initialValues={itemCharacteristics}
                onAccept={characteristicsChange}
            />,
        },
    ];

    const renderCharacteristicsSection = () => {
        return <Col24>
            <>
                <Divider />
                <Styles.CharCollapse defaultActiveKey={['1']} items={items} ghost />
                <Divider />
            </>
        </Col24>
    }

    const renderScheduleSection = () => (
        <Col24>
            <>
                <Styles.CharCollapse
                    items={[
                        {
                            key: "SCHEDULE",
                            label: <span><b>Horario de propuesta - ({scheduleEnabled ? itemSchedules.length || 1 : 0})</b></span>,
                            children: <>
                                <Form.Item
                                    name="hasSchedule"
                                    valuePropName="checked"
                                >
                                    <Checkbox
                                        checked={scheduleEnabled}
                                        onChange={(event) => {
                                            setScheduleEnabled(event.target.checked)
                                            if (!event.target.checked) {
                                                setItemSchedules([])
                                                form.setFieldsValue({ scheduleDays: [], scheduleNote: undefined })
                                            }
                                        }}
                                    >
                                        Mostrar horario en propuestas
                                    </Checkbox>
                                </Form.Item>
                                {
                                    scheduleEnabled &&
                                    <Row gutter={[16, 8]}>
                                        <Col24>
                                            <Form.Item
                                                label="Dias"
                                                name="scheduleDays"
                                                rules={[
                                                    {
                                                        validator: (_, value) => {
                                                            if (!scheduleEnabled || (value && value.length > 0)) {
                                                                return Promise.resolve()
                                                            }

                                                            return Promise.reject(new Error("Selecciona al menos un dia"))
                                                        },
                                                    },
                                                ]}
                                            >
                                                <Checkbox.Group options={scheduleDayOptions} />
                                            </Form.Item>
                                        </Col24>
                                        <Col24>
                                            <Form.Item
                                                label="Nota"
                                                name="scheduleNote"
                                            >
                                                <Input placeholder="Ej. El orden de los dias puede variar" />
                                            </Form.Item>
                                        </Col24>
                                    </Row>
                                }
                            </>,
                        },
                    ]}
                    ghost
                />
                <Divider />
            </>
        </Col24>
    )

    const renderForm = () => (
        <Form
            form={form}
            size="small"
            layout="vertical"
            requiredMark={false}
            autoComplete={'none'}
            onFinish={onFinish}
        >
            <Row gutter={[16, 8]}>
                <Col24>
                    <Form.Item
                        label="Servicio de catálogo"
                        name={"service"}
                        validateTrigger="onBlur"
                    >
                        <Select
                            placeholder="Selecciona un servicio o deja vacío para uno personalizado"
                            options={reduxProps.services?.map((item) => ({
                                value: item.id?.toString() || '',
                                label: `${item.serviceCategory?.toString()} - ${item.name.toString()}`,
                            }))}
                            filterOption={selectFilterOption}
                            showSearch
                            allowClear
                            onChange={handleServiceChange}
                        />
                    </Form.Item>
                </Col24>
                <Col24>
                    <Form.Item
                        label="Servicio cotizado"
                        name={"description"}
                        rules={[
                            getInputRequiredRule(descriptions.DETAIL.FORM.DESCRIPTION.LABEL),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <Input
                            placeholder={descriptions.DETAIL.FORM.DESCRIPTION.PLACEHOLDER}
                        />
                    </Form.Item>
                </Col24>
                {renderCharacteristicsSection()}
                {renderScheduleSection()}
                <Col8>
                    <Form.Item
                        label={descriptions.DETAIL.FORM.QTY.LABEL}
                        name={"qty"}
                        rules={[
                            getInputRequiredRule(descriptions.DETAIL.FORM.QTY.LABEL),
                            getPositiveNumberRule(descriptions.DETAIL.FORM.QTY.LABEL),
                        ]}
                        validateTrigger={["onBlur", "onChange"]}
                    >
                        <Input
                            placeholder={descriptions.DETAIL.FORM.QTY.PLACEHOLDER}
                            type="number"
                            onChange={handleQtyPriceChange}
                        />
                    </Form.Item>
                </Col8>
                <Col8>
                    <Form.Item
                        label={descriptions.DETAIL.FORM.PRICE.LABEL}
                        name={"price"}
                        rules={[
                            getInputRequiredRule(descriptions.DETAIL.FORM.PRICE.LABEL),
                            getPositiveNumberRule(descriptions.DETAIL.FORM.PRICE.LABEL),
                        ]}
                        validateTrigger={["onBlur", "onChange"]}
                    >
                        <CurrencyInput
                            placeholder={descriptions.DETAIL.FORM.PRICE.PLACEHOLDER}
                            onChange={handleQtyPriceChange}
                        />
                    </Form.Item>
                </Col8>
                <Col8>
                    <Form.Item
                        label={descriptions.DETAIL.FORM.TOTAL.LABEL}
                        name={"total"}
                        rules={[
                            getInputRequiredRule(descriptions.DETAIL.FORM.TOTAL.LABEL),
                            getPositiveNumberRule(descriptions.DETAIL.FORM.TOTAL.LABEL),
                        ]}
                        validateTrigger={["onBlur", "onChange"]}
                    >
                        <CurrencyInput
                            placeholder={descriptions.DETAIL.FORM.TOTAL.PLACEHOLDER}
                            readOnly
                        />
                    </Form.Item>
                </Col8>
                {renderButtons()}
            </Row>
            {/* {
                reduxProps.error &&
                <Alert
                    message={"ERROR"}
                    showIcon
                    description={reduxProps.error?.detail.message ?? commonDescriptions.FORM.UNKNOWN_ERROR}
                    type="error"
                    style={{
                        marginBottom: 12,
                    }}
                />
            }
            {!props.readOnly && renderButtons()} */}
        </Form>
    )

    return (
        renderForm()
    )
}
